import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Timer, BadgeDollarSign, Truck, ImagePlus, Loader2, CalendarClock } from "lucide-react";
import { useLanguage } from "@/i18n";
import { API, SLOT_STRINGS, CARRIERS, INSURANCE_STRINGS, TRACK_STRINGS } from "@/utils";

const WHY_ICONS = [Timer, BadgeDollarSign, Truck];
const LOCALES = { en: "en-US", pt: "pt-BR", es: "es-ES" };

const inputCls =
  "w-full bg-black/[0.04] border border-black/10 rounded-xl px-4 py-3.5 text-sm text-[#111111] placeholder-black/40 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300";

const chipCls = (active) =>
  `px-4 py-2 rounded-full border text-xs font-semibold whitespace-nowrap transition-colors duration-300 ${
    active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-black/15 text-black/55 hover:border-[#D4AF37]/60 hover:text-[#B8860B]"
  }`;

export const QuoteSection = () => {
  const { t, lang } = useLanguage();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", plate_vin: "", vehicle: "", service: "replacement", insurance: "", message: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [day, setDay] = useState(null);
  const [time, setTime] = useState(null);
  const [sending, setSending] = useState(false);

  const slotStrings = SLOT_STRINGS[lang] || SLOT_STRINGS.en;
  const insStrings = INSURANCE_STRINGS[lang] || INSURANCE_STRINGS.en;
  const locale = LOCALES[lang] || "en-US";
  const [takenSlots, setTakenSlots] = useState([]);

  useEffect(() => {
    axios.get(`${API}/slots/taken`).then((r) => setTakenSlots(r.data)).catch(() => {});
  }, []);

  const isoDay = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const days = useMemo(() => {
    const out = [];
    const d = new Date();
    while (out.length < 7) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() === 0) continue; // Sunday closed
      out.push(new Date(d));
    }
    return out;
  }, []);

  const timesFor = (date) => {
    const [start, end] = date.getDay() === 6 ? [10, 16] : [8, 19]; // Sat 10–16, Mon–Fri 8–19
    return Array.from({ length: end - start }, (_, i) => start + i);
  };

  const fmtDay = (d) => new Intl.DateTimeFormat(locale, { weekday: "short", month: "short", day: "numeric" }).format(d);
  const slotStr = day && time ? `${fmtDay(day)} · ${time}:00` : "";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t.quote.error);
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("language", lang);
      fd.append("slot", slotStr);
      fd.append("insurance", form.insurance);
      if (day) fd.append("slot_date", isoDay(day));
      if (time) fd.append("slot_time", `${time}:00`);
      if (photo) fd.append("photo", photo);
      const { data } = await axios.post(`${API}/leads`, fd);
      const codeLabel = (TRACK_STRINGS[lang] || TRACK_STRINGS.en).codeLabel;
      toast.success(t.quote.successTitle, {
        description: `${t.quote.successBody} ${codeLabel}: ${data.id.slice(0, 8)}`,
        duration: 10000,
      });
      setForm({ name: "", phone: "", email: "", plate_vin: "", vehicle: "", service: "replacement", insurance: "", message: "" });
      setPhoto(null);
      setPreview(null);
      setDay(null);
      setTime(null);
    } catch (err) {
      toast.error(t.quote.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="relative py-28 sm:py-36 bg-[#FAF9F6]" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37]">{t.quote.label}</span>
          </div>
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl leading-[1.05]">{t.quote.title}</h2>
          <p className="mt-6 text-black/65 text-base sm:text-lg leading-relaxed max-w-lg">{t.quote.sub}</p>

          <h3 className="mt-14 text-xs font-bold tracking-[0.25em] uppercase text-black/40">{t.quote.whyTitle}</h3>
          <div className="mt-6 space-y-6">
            {t.quote.why.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <motion.div
                  key={w.t}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className="flex items-start gap-4"
                >
                  <span className="w-11 h-11 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </span>
                  <div>
                    <div className="font-display font-semibold text-lg">{w.t}</div>
                    <div className="text-sm text-black/55 mt-0.5">{w.d}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="glass-panel rounded-3xl p-7 sm:p-10 shadow-2xl"
          data-testid="quote-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input className={inputCls} placeholder={t.quote.name} value={form.name} onChange={set("name")} data-testid="quote-name-input" required />
            <input className={inputCls} placeholder={t.quote.phone} value={form.phone} onChange={set("phone")} data-testid="quote-phone-input" required />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <input className={inputCls} type="email" placeholder={t.quote.email} value={form.email} onChange={set("email")} data-testid="quote-email-input" />
            <input className={inputCls} placeholder={t.quote.plate} value={form.plate_vin} onChange={set("plate_vin")} data-testid="quote-plate-input" />
          </div>
          <input className={`${inputCls} mt-4`} placeholder={t.quote.vehicle} value={form.vehicle} onChange={set("vehicle")} data-testid="quote-vehicle-input" />
          <select className={`${inputCls} mt-4 appearance-none`} value={form.service} onChange={set("service")} data-testid="quote-service-select">
            {Object.entries(t.quote.options).map(([k, v]) => (
              <option key={k} value={k} className="bg-white">{v}</option>
            ))}
          </select>
          <select className={`${inputCls} mt-4 appearance-none`} value={form.insurance} onChange={set("insurance")} data-testid="quote-insurance-select">
            <option value="" className="bg-white">{insStrings.placeholder}</option>
            {CARRIERS.map((c) => (
              <option key={c} value={c} className="bg-white">{c}</option>
            ))}
            <option value="not_sure" className="bg-white">{insStrings.notSure}</option>
            <option value="no_insurance" className="bg-white">{insStrings.none}</option>
          </select>

          <div className="mt-6" data-testid="slot-picker">
            <div className="flex items-center gap-2 mb-1.5">
              <CalendarClock className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-black/60">{slotStrings.label}</span>
            </div>
            <p className="text-[11px] text-black/35 mb-3">{slotStrings.hint}</p>
            <div className="flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {days.map((d, i) => (
                <button
                  type="button"
                  key={d.toISOString()}
                  onClick={() => { setDay(d); setTime(null); }}
                  className={chipCls(day?.toDateString() === d.toDateString())}
                  data-testid={`slot-day-${i}`}
                >
                  {fmtDay(d)}
                </button>
              ))}
            </div>
            {day && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="slot-times">
                {timesFor(day).filter((h) => !takenSlots.some((s) => s.date === isoDay(day) && s.time === `${h}:00`)).map((h) => (
                  <button
                    type="button"
                    key={h}
                    onClick={() => setTime(h)}
                    className={chipCls(time === h)}
                    data-testid={`slot-time-${h}`}
                  >
                    {h}:00
                  </button>
                ))}
              </div>
            )}
          </div>

          <textarea
            className={`${inputCls} mt-4 min-h-[96px] resize-none`}
            placeholder={t.quote.message}
            value={form.message}
            onChange={set("message")}
            data-testid="quote-message-input"
          />

          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="quote-photo-input" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="quote-photo-dropzone"
            className="mt-4 w-full border border-dashed border-black/20 hover:border-[#D4AF37] rounded-xl px-4 py-6 flex items-center justify-center gap-3 text-sm text-black/55 hover:text-[#D4AF37] transition-colors duration-300 overflow-hidden"
          >
            {preview ? (
              <>
                <img src={preview} alt="damage" className="w-12 h-12 object-cover rounded-lg" />
                <span>{t.quote.photoReady}</span>
              </>
            ) : (
              <>
                <ImagePlus className="w-5 h-5" />
                <span>{t.quote.photo}</span>
              </>
            )}
          </button>

          <button
            type="submit"
            disabled={sending}
            data-testid="quote-submit-button"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E6C965] disabled:opacity-60 text-black font-bold px-8 py-4 rounded-full text-base transition-colors duration-300 hover:scale-[1.02] transform"
          >
            {sending && <Loader2 className="w-5 h-5 animate-spin" />}
            {sending ? t.quote.sending : t.quote.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
